import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Building2, MessagesSquare, Plus, Search, Send, Users2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { MultiSelect } from '@/shared/components/ui';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useUser } from '@/shared/hooks/useAuth';
import { useChatConversations, useChatMessages, useChatMutations } from '../api';
import type { ChatConversation } from '../api';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

function timeLabel(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function dayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function conversationIcon(conversation: ChatConversation) {
  if (conversation.type === 'company') return <Building2 className="h-4 w-4" aria-hidden />;
  if (conversation.type === 'department' || conversation.type === 'group') return <Users2 className="h-4 w-4" aria-hidden />;
  return <MessagesSquare className="h-4 w-4" aria-hidden />;
}

/** WhatsApp-style internal messaging: DMs, groups, department rooms, company room. */
export function ChatPage() {
  const user = useUser();
  const { data: conversations = [], isLoading } = useChatConversations();
  const { startDm, createGroup, sendMessage, markRead } = useChatMutations();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dmModalOpen, setDmModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );
  const { data: messagePage, isLoading: messagesLoading } = useChatMessages(selectedId);
  const messages = useMemo(() => messagePage?.messages ?? [], [messagePage]);

  // Mark as read when opening a conversation or when new messages arrive while open.
  const lastMarkedRef = useRef<string>('');
  useEffect(() => {
    if (!selectedId || messages.length === 0) return;
    const marker = `${selectedId}:${messages[messages.length - 1]?.id ?? ''}`;
    if (lastMarkedRef.current !== marker) {
      lastMarkedRef.current = marker;
      markRead.mutate(selectedId);
    }
  }, [selectedId, messages, markRead]);

  // Keep the view pinned to the newest message.
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages.length, selectedId]);

  const visibleConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => c.name.toLowerCase().includes(q));
  }, [conversations, search]);

  const send = () => {
    const content = draft.trim();
    if (!content || !selectedId) return;
    setDraft('');
    sendMessage.mutate(
      { conversationId: selectedId, content },
      { onError: (err) => toast.error(errorMessage(err, 'Message failed to send.')) },
    );
  };

  return (
    <div className="flex h-full flex-col p-4 md:p-6">
      <PageHeader
        icon={MessagesSquare}
        eyebrow="Workspace"
        title="ZYNX Chat"
        description={<>Direct messages, private groups, and public department &amp; company rooms</>}
        actions={
          <><div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDmModalOpen(true)}>
                    <MessagesSquare className="h-4 w-4" aria-hidden />
                    New DM
                  </Button>
                  <Button onClick={() => setGroupModalOpen(true)}>
                    <Plus className="h-4 w-4" aria-hidden />
                    New Group
                  </Button>
                </div></>
        }
      />

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden rounded-lg border border-outline md:grid-cols-[320px_1fr]">
        {/* ── Conversation list ── */}
        <div className="flex min-h-0 flex-col border-r border-outline bg-surface">
          <div className="border-b border-outline p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" aria-hidden />
              <input
                aria-label="Search conversations"
                className="h-9 w-full rounded border border-outline bg-surface pl-9 pr-3 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" role="list" aria-label="Conversations">
            {isLoading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner />
              </div>
            ) : visibleConversations.length === 0 ? (
              <p className="p-4 text-body-sm text-on-surface-variant">No conversations found.</p>
            ) : (
              visibleConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  role="listitem"
                  onClick={() => setSelectedId(conversation.id)}
                  className={`flex w-full items-center gap-3 border-b border-outline/60 px-3 py-2.5 text-left transition-colors ${
                    selectedId === conversation.id ? 'bg-primary/5' : 'hover:bg-surface-variant/60'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      conversation.type === 'company'
                        ? 'bg-primary/10 text-primary'
                        : conversation.type === 'department'
                          ? 'bg-info/10 text-info-text'
                          : 'bg-surface-variant text-on-surface-variant'
                    }`}
                  >
                    {conversationIcon(conversation)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium text-on-surface">{conversation.name}</span>
                      <span className="shrink-0 text-[11px] text-on-surface-variant">
                        {timeLabel(conversation.lastMessageAt)}
                      </span>
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-body-sm text-on-surface-variant">
                        {conversation.lastMessagePreview
                          ? `${conversation.lastMessageByName ? `${conversation.lastMessageByName}: ` : ''}${conversation.lastMessagePreview}`
                          : conversation.type === 'department'
                            ? 'Department room'
                            : conversation.type === 'company'
                              ? 'Everyone at ZYNX'
                              : 'No messages yet'}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-on-primary">
                          {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Messages pane ── */}
        <div className="flex min-h-0 flex-col bg-surface-variant/30">
          {!selected ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <EmptyState
                icon={<MessagesSquare className="h-8 w-8" aria-hidden />}
                title="Select a conversation"
                description="Pick a chat on the left, start a DM, or open your department room."
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-outline bg-surface px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {conversationIcon(selected)}
                  </span>
                  <div>
                    <p className="font-medium text-on-surface">{selected.name}</p>
                    <p className="text-body-sm text-on-surface-variant">
                      {selected.type === 'company'
                        ? 'Public — everyone at ZYNX'
                        : selected.type === 'department'
                          ? 'Public department room'
                          : selected.type === 'group'
                            ? `Private group · ${selected.memberCount ?? 0} members`
                            : 'Direct message'}
                    </p>
                  </div>
                </div>
                {selected.type !== 'dm' && (
                  <Badge tone={selected.type === 'company' ? 'primary' : 'info'}>
                    {selected.type === 'company' ? 'Company' : selected.type === 'department' ? 'Department' : 'Group'}
                  </Badge>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-4" aria-label="Messages">
                {messagesLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <Spinner />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-body-sm text-on-surface-variant">
                    No messages yet — say hello!
                  </p>
                ) : (
                  <ol className="flex flex-col gap-1.5">
                    {messages.map((message, index) => {
                      const previous = messages[index - 1];
                      const newDay =
                        !previous ||
                        new Date(previous.createdAt).toDateString() !== new Date(message.createdAt).toDateString();
                      const sameSenderAsPrevious =
                        !!previous && previous.senderId === message.senderId && !newDay;
                      return (
                        <li key={message.id}>
                          {newDay && (
                            <p className="my-3 text-center">
                              <span className="rounded-full bg-surface px-3 py-1 text-[11px] text-on-surface-variant shadow-sm">
                                {dayLabel(message.createdAt)}
                              </span>
                            </p>
                          )}
                          <div className={`flex ${message.mine ? 'justify-end' : 'justify-start'}`}>
                            <div
                              className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${
                                message.mine
                                  ? 'rounded-br-md bg-primary text-on-primary'
                                  : 'rounded-bl-md bg-surface text-on-surface'
                              }`}
                            >
                              {!message.mine && !sameSenderAsPrevious && selected.type !== 'dm' && (
                                <p className="mb-0.5 text-[11px] font-semibold text-primary">{message.senderName}</p>
                              )}
                              <p className="whitespace-pre-wrap break-words text-data-md">{message.content}</p>
                              <p
                                className={`mt-0.5 text-right text-[10px] ${
                                  message.mine ? 'text-on-primary/70' : 'text-on-surface-variant'
                                }`}
                              >
                                {new Date(message.createdAt).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
                <div ref={bottomRef} />
              </div>

              <form
                className="flex items-end gap-2 border-t border-outline bg-surface p-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
              >
                <textarea
                  aria-label={`Message ${selected.name}`}
                  rows={1}
                  className="max-h-32 min-h-10 flex-1 resize-y rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="Type a message"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <Button type="submit" disabled={!draft.trim() || sendMessage.isPending} aria-label="Send message">
                  <Send className="h-4 w-4" aria-hidden />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <StartDmModal
        open={dmModalOpen}
        currentUserId={user?.id ?? ''}
        onClose={() => setDmModalOpen(false)}
        onStart={(userId) =>
          startDm.mutate(userId, {
            onSuccess: (conversation) => {
              setSelectedId(conversation.id);
              setDmModalOpen(false);
            },
            onError: (err) => toast.error(errorMessage(err, 'Could not start the conversation.')),
          })
        }
        pending={startDm.isPending}
      />
      <CreateGroupModal
        open={groupModalOpen}
        currentUserId={user?.id ?? ''}
        onClose={() => setGroupModalOpen(false)}
        onCreate={(name, memberIds) =>
          createGroup.mutate(
            { name, memberIds },
            {
              onSuccess: (conversation) => {
                setSelectedId(conversation.id);
                setGroupModalOpen(false);
                toast.success('Group created.');
              },
              onError: (err) => toast.error(errorMessage(err, 'Could not create the group.')),
            },
          )
        }
        pending={createGroup.isPending}
      />
    </div>
  );
}

/* ── Start DM modal ──────────────────────────────────────────────────────── */

function StartDmModal({
  open,
  currentUserId,
  onClose,
  onStart,
  pending,
}: {
  open: boolean;
  currentUserId: string;
  onClose: () => void;
  onStart: (userId: string) => void;
  pending: boolean;
}) {
  const { data: users = [] } = useUserOptions(open);
  const [userId, setUserId] = useState('');
  const options = users
    .filter((u) => u.id !== currentUserId)
    .map((u) => ({ label: `${u.display_name}${u.department ? ` (${u.department})` : ''}`, value: u.id }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Direct Message"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!userId || pending} onClick={() => onStart(userId)}>
            Start Chat
          </Button>
        </div>
      }
    >
      <Select
        label="Team member"
        name="dm-user"
        options={options}
        placeholder="Select a person"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />
    </Modal>
  );
}

/* ── Create group modal ──────────────────────────────────────────────────── */

function CreateGroupModal({
  open,
  currentUserId,
  onClose,
  onCreate,
  pending,
}: {
  open: boolean;
  currentUserId: string;
  onClose: () => void;
  onCreate: (name: string, memberIds: string[]) => void;
  pending: boolean;
}) {
  const { data: users = [] } = useUserOptions(open);
  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const options = users
    .filter((u) => u.id !== currentUserId)
    .map((u) => ({ label: `${u.display_name}${u.department ? ` (${u.department})` : ''}`, value: u.id }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Group"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || memberIds.length === 0 || pending}
            onClick={() => onCreate(name.trim(), memberIds)}
          >
            Create Group
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input label="Group name" name="group-name" value={name} onChange={(e) => setName(e.target.value)} />
        <MultiSelect
          label="Members"
          options={options}
          value={memberIds}
          onChange={setMemberIds}
          placeholder="Select members…"
        />
        <p className="text-body-sm text-on-surface-variant">
          You will be the group admin. Department and company rooms are created automatically — no need to
          make those here.
        </p>
      </div>
    </Modal>
  );
}
